// LeadAgentAcceptance.functions.ts contains pure helpers for the Lead Agent
// pre-acceptance flow (RET-1230). Kept out of components/provider for
// testability and the react-refresh/only-export-components rule.

import moment from 'moment';
import {
  ePlanCode,
  eSubscriptionStatus,
} from 'src/providers/SubscriptionProvider/Subscription.enums';
import { isProOrAdvancedPlan } from 'src/providers/SubscriptionProvider/Subscription.utils';
import type {
  tLeadAgentAcceptance,
  tLeadAgentAcceptanceMetrics,
  tLeadAgentAcceptanceOrigin,
  tLeadAgentAcceptanceState,
  tLeadAgentBannerVariant,
  tLeadAgentExpiredPhase,
  tLeadAgentPaidUsageBand,
  tLeadAgentReactivatePreview,
  tLeadAgentReactivateResult,
  tLeadAgentReactivateStatus,
  tLeadAgentRenewalPhase,
  tLeadAgentSelectableTier,
  tLeadAgentTier,
  tLeadAgentTierChangePreview,
  tLeadAgentTierChangeResult,
  tLeadAgentTierChangeStatus,
  tLeadAgentTierFit,
  tLeadAgentUnlockOffer,
  tLeadAgentUnlockOption,
  tLeadAgentUnlockPhase,
  tLeadAgentUnlockResult,
  tLeadAgentUnlockTierOffer,
  tLeadAgentUsage,
  tLeadAgentWorkspaceBucket,
} from './LeadAgentAcceptance.types';

/**
 * RET-1230 banner variant thresholds, measured against the workspace's
 * PERSONAL first-charge date (`nextPaymentAt`), never against the global
 * 15-jun flip. These thresholds are specific to the acceptance banner and
 * are intentionally independent of any free-period countdown logic.
 */
export const LEAD_AGENT_FOMO_THRESHOLD_DAYS = 14;
export const LEAD_AGENT_URGENT_THRESHOLD_DAYS = 3;

/**
 * C7 (RET-1343) vencido degradation thresholds, measured in whole UTC days
 * SINCE the workspace's `nextPaymentAt` (the deactivation anchor — A10 set
 * `deactivatedAt = next_payment_at`). Day 0 (the charge day) is the first
 * expired day. Inclusive upper bounds (`<=`) mirror `resolveBannerVariant`'s
 * `>`-style convention so day 7 is the last prominent day and day 30 the last
 * discreet day. Values from the C7 prototype (lead-agent-flujo_1.html).
 */
export const LEAD_AGENT_EXPIRED_PROMINENT_MAX_DAYS = 7;
export const LEAD_AGENT_EXPIRED_COMPACT_MAX_DAYS = 14;
export const LEAD_AGENT_EXPIRED_DISCREET_MAX_DAYS = 30;

/**
 * C8 (RET-1344) usage banner amber threshold: capacity at or above this percent
 * (but below 100) is "near limit". 100% is "capped" (bot paused). Single source
 * of truth — the server owns the 100% boundary (`isPaused`/`capacityPct`), the
 * FE owns only this amber threshold, so it must not be duplicated elsewhere.
 */
export const LEAD_AGENT_USAGE_NEAR_LIMIT_PCT = 80;

/** C8 usage banner phase derived from capacity. Null when there is no cap. */
export type tLeadAgentUsagePhase = 'capped' | 'nearLimit' | null;

/**
 * Resolve the C8 usage phase. `capped` takes precedence over `nearLimit`.
 * Capped is driven by the SERVER's authoritative `isPaused` signal OR
 * `capacityPct >= 100` — the percent is rounded server-side, so a workspace the
 * server has actually paused (bot stopped at the cap, B5) must show the capped
 * copy even if its rounded percent reads just under 100. `nearLimit` is the
 * amber band (>= threshold, < 100, not paused). Below the threshold, or when
 * there is no cap, returns null (no usage banner). Pure — callers add the
 * role/mutual-exclusion gate.
 *
 * RET-1882 — `isTrialCap` yields the whole state to the U9 trial family. The
 * banner hierarchy is not ours to pick: RET-1633's dev guide (§4 UX) orders it
 * "bot pausado > bloqueo inminente > cuenta regresiva > vencido", and the SSOT
 * §18 settles what the two banners may promise — the 140M trial cap applies to
 * pre-accepted workspaces too, and only the paid unlock raises it. So on a
 * trial-tagged row every C8 sentence is false: the cap is not "the plan's", and
 * the "amplía tu tier" CTA cannot resume the bot. Required, not optional, so no
 * caller can inherit the paid-workspace reading by default. Feeds three
 * surfaces at once — the standalone banner plus `la-usage-capped` and
 * `la-usage-near`, whose percent AlertCenter.hook derives from this phase.
 */
export function resolveUsagePhase(options: {
  capacityPct: number;
  hasCap: boolean;
  isPaused: boolean;
  isTrialCap: boolean;
}): tLeadAgentUsagePhase {
  if (options.isTrialCap) return null;
  if (!options.hasCap) return null;
  if (options.isPaused || options.capacityPct >= 100) return 'capped';
  if (options.capacityPct >= LEAD_AGENT_USAGE_NEAR_LIMIT_PCT)
    return 'nearLimit';
  return null;
}

/**
 * RET-2226 — the band the Automatizaciones usage indicator paints. Scoped to
 * the C8 cohort the usage banner reads (no acceptance row: an ACCEPTED
 * workspace 404s), and yields to the trial family on a trial-tagged row like
 * C8 does (RET-1882). The amber and capped boundaries come from
 * `resolveUsagePhase` itself, so the indicator can never disagree with the
 * banner or the `la-usage-*` alerts about which band a workspace is in.
 */
export function resolvePaidUsageBand(options: {
  usage: tLeadAgentUsage | null;
  hasAcceptanceRow: boolean;
}): tLeadAgentPaidUsageBand | null {
  const { usage } = options;
  if (options.hasAcceptanceRow || usage === null) return null;
  if (!usage.hasCap || usage.isTrialCap) return null;
  return (
    resolveUsagePhase({
      capacityPct: usage.capacityPct,
      hasCap: usage.hasCap,
      isPaused: usage.isPaused,
      isTrialCap: usage.isTrialCap,
    }) ?? 'onTrack'
  );
}

export const LEAD_AGENT_TIERS: readonly tLeadAgentTier[] = [
  'STARTER',
  'GROWTH',
  'SCALE',
  'NONE',
];

/**
 * Tiers the Owner can actually pick — `NONE` is a DB sentinel, never selectable.
 * Ordered ascending (STARTER < GROWTH < SCALE); `resolveUpgradeableTiers` relies
 * on the index as the ordinal. Single source of truth shared by the banner, the
 * container, and all three modals — keep membership/order checks against this
 * constant, never inline tier literals.
 */
export const LEAD_AGENT_SELECTABLE_TIERS: readonly tLeadAgentSelectableTier[] =
  ['STARTER', 'GROWTH', 'SCALE'];

/**
 * The higher tiers the Owner may upgrade INTO from `currentTier`, in ascending
 * order (C5 / RET-1304, RET-1614). Upgrade-only: never returns the current tier
 * or a lower one (downgrade is support-only). Fails CLOSED on an unknown /
 * `NONE` / null / undefined current tier — returns `[]` (empty state) rather
 * than the full set: if we cannot positively identify the current tier we
 * cannot know what an upgrade is, and offering STARTER to a GROWTH/SCALE
 * workspace would be a same/lower "upgrade" (double-discount / negative
 * proration / 422 vector). Consistent with the rest of the feature's
 * fail-closed posture; the backend 422 is the last line, not the first.
 */
export function resolveUpgradeableTiers(
  currentTier: tLeadAgentTier | null | undefined,
): tLeadAgentSelectableTier[] {
  const currentIndex = LEAD_AGENT_SELECTABLE_TIERS.indexOf(
    currentTier as tLeadAgentSelectableTier,
  );
  if (currentIndex === -1) return [];
  return LEAD_AGENT_SELECTABLE_TIERS.slice(currentIndex + 1);
}

/**
 * Monthly token cap of each tier in millions, mirroring leadbilling's
 * `LeadAgentTier::tokenCap()` (Starter 130M / Growth 450M / Scale 1,200M since
 * RET-2140). Only the ratios below leave this module.
 */
const LEAD_AGENT_TIER_TOKEN_CAP_MILLIONS: Readonly<
  Record<tLeadAgentSelectableTier, number>
> = {
  STARTER: 130,
  GROWTH: 450,
  SCALE: 1200,
};

/**
 * Monthly token cap of each tier expressed as a MULTIPLE of the base (Starter)
 * cap, derived from `LEAD_AGENT_TIER_TOKEN_CAP_MILLIONS` (1× / ~3.46× / ~9.23×).
 *
 * The front deliberately exposes the RATIO, not the absolute token counts: the
 * customer never sees tokens (every surface speaks in "% of your plan's
 * capacity", per the C8 usage banner). The caps are no longer proportional, so
 * the ratio is derived from the absolute caps instead of being typed by hand —
 * when the backend enum changes, update the caps above, not these numbers
 * (RET-2179: the 4× / 12× literals outlived the RET-2140 cap raise).
 */
export const LEAD_AGENT_TIER_CAPACITY_MULTIPLE: Readonly<
  Record<tLeadAgentSelectableTier, number>
> = {
  STARTER: 1,
  GROWTH:
    LEAD_AGENT_TIER_TOKEN_CAP_MILLIONS.GROWTH /
    LEAD_AGENT_TIER_TOKEN_CAP_MILLIONS.STARTER,
  SCALE:
    LEAD_AGENT_TIER_TOKEN_CAP_MILLIONS.SCALE /
    LEAD_AGENT_TIER_TOKEN_CAP_MILLIONS.STARTER,
};

/**
 * How many times bigger `targetTier` is than `currentTier` (C5 upgrade modal).
 *
 * This is the number the upgrade modal must show, NOT the tier's absolute
 * multiple: "9.2× more capacity" is anchored to Starter, so it is plainly FALSE
 * for a Growth workspace moving to Scale (that is ~2.67×, 1,200M/450M). Returns null
 * when either tier is unknown / `NONE`, or when the ratio is not an upgrade
 * (<= 1), so the caller omits the claim instead of rendering a wrong or absurd
 * one.
 */
export function resolveCapacityMultiple(
  currentTier: tLeadAgentTier | null | undefined,
  targetTier: tLeadAgentSelectableTier,
): number | null {
  const current =
    LEAD_AGENT_TIER_CAPACITY_MULTIPLE[currentTier as tLeadAgentSelectableTier];
  const target = LEAD_AGENT_TIER_CAPACITY_MULTIPLE[targetTier];
  if (!current || !target) return null;

  const multiple = target / current;
  return multiple > 1 ? multiple : null;
}

/**
 * Renders a capacity multiple for the upgrade modal chip ("3.4× tu capacidad
 * actual"). The ratios are not integers since RET-2140, and the raw float would
 * print as "3.4615384615384617".
 *
 * FLOORS to one decimal instead of rounding: this is a purchase screen, so the
 * claim may understate the capacity the customer buys, never overstate it
 * (450/130 = 3.46 shows 3.4, not 3.5). The epsilon keeps a float that lands a
 * hair under an exact tenth (2.9999999) from dropping a whole tenth. Formatted
 * with `Intl` so pt-BR gets its decimal comma.
 */
export function formatCapacityMultiple(
  multiple: number,
  locale: string,
): string {
  const floored = Math.floor(multiple * 10 + 1e-9) / 10;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 1 }).format(
    floored,
  );
}

/**
 * Re-expresses this month's consumption against a BIGGER tier's cap: the same
 * absolute usage, measured as a percentage of the target tier's capacity.
 *
 * Used by the upgrade modal's capacity meter, which shows the customer's live
 * `usageCapacityPct` (e.g. 92% of Growth) next to what that same consumption
 * would be on the target tier (35% of Scale). Returns null when the inputs are
 * unusable so the meter is simply not painted — it is decoration for a decision,
 * never a gate on the charge.
 *
 * Rounds to the nearest integer to match the usage banner, which also renders
 * an integer percentage.
 */
export function projectCapacityPct(
  currentPct: number | null | undefined,
  capacityMultiple: number | null,
): number | null {
  if (currentPct === null || currentPct === undefined) return null;
  if (!Number.isFinite(currentPct) || currentPct < 0) return null;
  if (!capacityMultiple || capacityMultiple <= 0) return null;

  return Math.round(currentPct / capacityMultiple);
}

/**
 * The upgrade to put in front of the customer: the SMALLEST tier whose cap
 * survives the rest of the period at the current pace.
 *
 * Not "the next tier up" — that is a guess. A workspace burning 88% of Starter in
 * 27 days needs Growth; one burning 99% in 5 days needs Scale, and offering it
 * Growth would leave the agent mute again this month. Anything bigger than the
 * first tier that lasts is overselling.
 *
 * Falls back to the smallest step when no projection can be made (the pace is
 * unknown, so we cannot claim one tier solves it), and to the LARGEST available
 * tier when none of them survives the pace — that is the best we can offer.
 * Returns null when there is nothing to upgrade to.
 */
export function resolveSuggestedUpgradeTier(options: {
  currentTier: tLeadAgentTier | null | undefined;
  capacityPct: number | null | undefined;
  resetDate: string | null | undefined;
  now?: Date;
}): tLeadAgentSelectableTier | null {
  const upgradeable = resolveUpgradeableTiers(options.currentTier);
  if (upgradeable.length === 0) return null;

  for (const tier of upgradeable) {
    const multiple = resolveCapacityMultiple(options.currentTier, tier);
    const burn = projectCapacityBurn({
      capacityPct: options.capacityPct,
      resetDate: options.resetDate,
      capacityMultiple: multiple ?? 1,
      now: options.now,
    });
    // Un-projectable inputs do not depend on the tier, so bail once.
    if (burn === null) return upgradeable[0];
    if (burn.status === 'lasts') return tier;
  }

  return upgradeable[upgradeable.length - 1];
}

const MS_PER_DAY = 24 * 60 * 60 * 1000;

/**
 * What the current burn rate implies for the rest of the cap period.
 *
 * - `exhausted`: the cap is already spent (the bot is paused right now). No
 *   projection needed — it is a fact, not a forecast.
 * - `runs_out`:  at this rate the cap dies BEFORE the reset, leaving the agent
 *   mute for the gap. `exhaustionDate` is when.
 * - `lasts`:     at this rate it survives the whole period.
 */
export type tCapacityBurnProjection =
  | { status: 'exhausted' }
  | { status: 'runs_out'; exhaustionDate: string }
  | { status: 'lasts' };

/**
 * Extrapolates the workspace's consumption to the end of the cap period, for the
 * CURRENT cap or for a bigger one (`capacityMultiple`).
 *
 * Everything is derived from the only two usage fields the client is given —
 * `capacityPct` and `resetDate` — because the raw token counts deliberately never
 * cross the wire (RET-1592). The period start is `resetDate - 1 month`: the Lead
 * Agent cap is non-cumulative and resets MONTHLY regardless of the Pro/Advanced
 * billing frequency (`RenewMonthlyFeaturesSubscriptionsUseCase`), so one month is
 * the period length in every case, and moment handles the uneven month lengths.
 *
 * The model is LINEAR by decision: consumption is spread evenly across the days
 * elapsed and bursts are NOT suppressed. A workspace that burns 92% of its cap in
 * three days is told its quota dies almost immediately — because at that pace it
 * does. Withholding the projection would deny the warning to precisely the
 * accounts closest to going mute, so the projection is always made whenever the
 * inputs allow it.
 *
 * A trailing-window rate would weight recent behaviour better, but that needs a
 * new derived field from the backend (not the tokens — just the rate).
 *
 * Returns null whenever the projection cannot be made honestly, so the caller
 * renders nothing rather than a guess.
 */
export function projectCapacityBurn(options: {
  capacityPct: number | null | undefined;
  resetDate: string | null | undefined;
  /** 1 (default) = the current cap; >1 = the cap of a higher tier. */
  capacityMultiple?: number;
  /** Injectable for tests; defaults to now. */
  now?: Date;
}): tCapacityBurnProjection | null {
  const { capacityPct, resetDate, capacityMultiple = 1 } = options;

  if (capacityPct === null || capacityPct === undefined) return null;
  if (!Number.isFinite(capacityPct) || capacityPct <= 0) return null;
  if (!capacityMultiple || capacityMultiple <= 0) return null;
  if (!resetDate) return null;

  const reset = moment(resetDate);
  if (!reset.isValid()) return null;

  const now = options.now ? moment(options.now) : moment();
  const periodStart = reset.clone().subtract(1, 'month');

  const elapsedDays = now.diff(periodStart, 'days', true);
  const periodDays = reset.diff(periodStart, 'days', true);
  // Correctness guards only, NOT a behavioural floor: zero/negative elapsed time
  // has no rate to divide by, and a reset that already passed (stale mirror) or a
  // `now` outside the period makes the whole window meaningless.
  if (elapsedDays <= 0) return null;
  if (elapsedDays >= periodDays) return null;

  // The percentage is always against the CURRENT cap, so a bigger cap consumes a
  // proportionally smaller slice of it.
  const effectivePct = capacityPct / capacityMultiple;
  if (effectivePct >= 100) return { status: 'exhausted' };

  const pctPerDay = effectivePct / elapsedDays;
  const daysToExhaust = (100 - effectivePct) / pctPerDay;

  // Milliseconds, NOT `moment.add(daysToExhaust, 'days')`: moment rounds a
  // fractional day count, which pushed the exhaustion past the reset and reported
  // "it lasts" for caps that die hours before it (88% on day 27 needs 3.68 more
  // days and the reset is 4 away — rounding to 4 flipped the verdict). The bias
  // was always toward under-warning, so it hid exactly the cases worth showing.
  const exhaustionMs = now.valueOf() + daysToExhaust * MS_PER_DAY;

  if (exhaustionMs >= reset.valueOf()) return { status: 'lasts' };

  return {
    status: 'runs_out',
    exhaustionDate: new Date(exhaustionMs).toISOString(),
  };
}

/**
 * The offer row's tier is the suggested one; fall back to GROWTH when it is
 * unset or not a selectable tier (e.g. the `NONE` DB sentinel). Single source
 * of truth shared by the banner, the container, and both modals — keep the
 * membership check against the constant, never inline tier literals.
 */
export function resolveSuggestedTier(
  tier: string | undefined,
): tLeadAgentSelectableTier {
  return LEAD_AGENT_SELECTABLE_TIERS.includes(tier as tLeadAgentSelectableTier)
    ? (tier as tLeadAgentSelectableTier)
    : 'GROWTH';
}

const LEAD_AGENT_STATES: readonly tLeadAgentAcceptanceState[] = [
  'NONE',
  'ACCEPTED',
  'PAUSED',
  'NOT_IN_PRESALE',
  // RET-2153 — leadbilling (PR #1237) reconstructs the `state='inactive' +
  // accepted=true` row as this literal, for a permission that WAS accepted
  // and was later revoked. Without it in the whitelist,
  // `parseLeadAgentAcceptance` discards the row (its documented fail-safe
  // for an unknown state) and every purchase-door surface stays dark for a
  // customer who already bought once.
  'REVOKED',
];

const WORKSPACE_BUCKETS: readonly tLeadAgentWorkspaceBucket[] = [
  'active',
  'dormant',
];

const LEAD_AGENT_ORIGINS: readonly tLeadAgentAcceptanceOrigin[] = [
  'PRESALE_COHORT',
  'POST_LAUNCH',
];

const LEAD_AGENT_RENEWAL_PHASES: readonly tLeadAgentRenewalPhase[] = [
  'before_renewal',
  'renewal_in_progress',
  'after_renewal',
  'trial_ended',
];

function parseLeadAgentRenewalPhase(
  value: unknown,
): tLeadAgentRenewalPhase | null {
  const phase = toNullableString(value);
  return LEAD_AGENT_RENEWAL_PHASES.includes(phase as tLeadAgentRenewalPhase)
    ? (phase as tLeadAgentRenewalPhase)
    : null;
}

/**
 * Whole calendar days from `now` to `nextPaymentAt` (start-of-day to
 * start-of-day, so the value is stable across the day). Negative when the
 * date is already past. Null for missing/invalid dates.
 *
 * Both endpoints are bucketed by UTC calendar day so the countdown stays
 * consistent with `formatPaymentDate` (LeadAgentAcceptanceBanner.functions.ts),
 * which renders the anchor in UTC. The B7 populator emits `nextPaymentAt`
 * anchors as UTC midnight (e.g. "2026-06-10T00:00:00Z"); parsing those in
 * local time from a negative-offset zone (CST, UTC-06:00) would push the
 * start-of-day back a calendar day and make the countdown one day short of
 * the date shown to the user.
 */
export function daysUntilPayment(
  nextPaymentAt: string | null,
  now: Date = new Date(),
): number | null {
  if (!nextPaymentAt) return null;
  const target = moment.utc(nextPaymentAt);
  if (!target.isValid()) return null;
  return target.startOf('day').diff(moment.utc(now).startOf('day'), 'days');
}

/**
 * Variant switch (RET-1230, Fanny 2026-06-01): informative >14d,
 * fomo 14-4d, urgent <=3d. Unknown date falls back to informative
 * (no countdown to show). Past-due stays urgent.
 */
export function resolveBannerVariant(
  days: number | null,
): tLeadAgentBannerVariant {
  if (days === null) return 'informative';
  if (days > LEAD_AGENT_FOMO_THRESHOLD_DAYS) return 'informative';
  if (days > LEAD_AGENT_URGENT_THRESHOLD_DAYS) return 'fomo';
  return 'urgent';
}

/**
 * Whole UTC calendar days SINCE `nextPaymentAt` — the inverse of
 * `daysUntilPayment`, sharing its UTC start-of-day bucketing (do NOT
 * reintroduce a local-time parse or the documented CST off-by-one reopens).
 * `>= 0` once the charge day has arrived (day 0 = charge day = first expired
 * day per A10), negative before it, null for missing/invalid dates.
 */
export function daysSincePayment(
  nextPaymentAt: string | null,
  now: Date = new Date(),
): number | null {
  const until = daysUntilPayment(nextPaymentAt, now);
  if (until === null) return null;
  // Negating 0 yields -0, which Object.is (and toBe) treats as distinct from 0.
  return until === 0 ? 0 : -until;
}

/**
 * C7 (RET-1343 / RET-2129): the Lead Agent is "vencido" when the row is still
 * pending and the backend says the trial ended. During the additive rollout,
 * an absent phase keeps the previous UTC-day computation as a compatibility
 * fallback. Display/countdown formatting continues to use `nextPaymentAt`.
 */
export function isLeadAgentExpired(
  acceptance: tLeadAgentAcceptance | null,
  now: Date = new Date(),
): boolean {
  if (acceptance?.state !== 'NONE') return false;
  if (acceptance.phase) return acceptance.phase === 'trial_ended';
  const since = daysSincePayment(acceptance.nextPaymentAt, now);
  return since !== null && since >= 0;
}

/**
 * Maps days-since-charge to the C7 anti-fatigue phase, or null when not yet
 * expired (negative/null days). Inclusive upper bounds keep day 7 prominent
 * and day 30 discreet (see threshold constants).
 */
export function resolveExpiredPhase(
  daysSince: number | null,
): tLeadAgentExpiredPhase | null {
  if (daysSince === null || daysSince < 0) return null;
  if (daysSince <= LEAD_AGENT_EXPIRED_PROMINENT_MAX_DAYS) return 'prominent';
  if (daysSince <= LEAD_AGENT_EXPIRED_COMPACT_MAX_DAYS) return 'compact';
  if (daysSince <= LEAD_AGENT_EXPIRED_DISCREET_MAX_DAYS) return 'discreet';
  return 'retired';
}

/**
 * RET-1637 / RET-2129 — the Owner dropped the add-on but it stays active until
 * the trial ends. The backend phase is authoritative when present; every phase
 * except `trial_ended` remains inside the full-day trial window. During rollout,
 * an absent phase keeps the previous UTC-day computation as a fallback.
 *
 * PAUSED alone (regardless of cut-off) drives the Subscription reactivation card
 * via `isLeadAgentPaused` — reactivation is offered both before and after the
 * cut-off. `everUsed` is NOT a factor: PAUSED means the add-on was dropped and
 * stays active until the cut-off, whether or not the bot had responded yet.
 *
 * "Not passed" uses `daysUntilPayment >= 0` — same UTC start-of-day bucketing as
 * `isLeadAgentExpired`/`daysSincePayment`, so the countdown stays consistent with
 * the rendered date and the day-0 (cut-off day) boundary. Owner gating, plan
 * eligibility, and the session-resolve latch live in the provider (mirroring
 * `isExpiredForOwner`).
 *
 * RET-2152 — this helper's missing-data answer (no `phase`, no `nextPaymentAt`)
 * is `false` ("not verifiably within the trial"), which is the SAFE direction
 * for the surfaces it drives (the pre-cutoff shutdown countdown: when unsure,
 * do not claim "still active"). That direction is wrong for a caller that needs
 * "the cut-off has POSITIVELY passed" (the purchase-door gate) — negating this
 * function's result for that purpose turns its fail-closed `false` into a
 * fail-OPEN `true`. That caller wants `isLeadAgentPausedAndLapsed` below
 * instead, never `!isLeadAgentPausedWithinTrial(...)`.
 */
export function isLeadAgentPausedWithinTrial(
  acceptance: tLeadAgentAcceptance | null,
  options: { now?: Date } = {},
): boolean {
  if (acceptance?.state !== 'PAUSED') return false;
  if (acceptance.phase) return acceptance.phase !== 'trial_ended';
  const until = daysUntilPayment(
    acceptance.nextPaymentAt,
    options.now ?? new Date(),
  );
  return until !== null && until >= 0;
}

/**
 * RET-2152 (C3 / Defecto 1) — the symmetric counterpart to
 * `isLeadAgentPausedWithinTrial`, built to `isLeadAgentExpired`'s fail-closed
 * shape instead: true only with POSITIVE evidence that a PAUSED row's cut-off
 * has already passed (backend `phase: 'trial_ended'`, or a `nextPaymentAt` in
 * the past). Same UTC start-of-day bucketing as `isLeadAgentExpired` (via
 * `daysSincePayment`) so the two agree on the day-0 boundary.
 *
 * Why this exists instead of `!isLeadAgentPausedWithinTrial(...)`: that helper
 * was written for the shutdown countdown, where an unknown cut-off must answer
 * "not verifiably within the trial" (`false`) — the safe direction for a banner
 * that only INFORMS. Negating it for a gate that ENABLES A CHARGE inverts that
 * safety: on a PAUSED row with no `phase` and no `nextPaymentAt` (the type
 * allows `nextPaymentAt: string | null`), `isLeadAgentPausedWithinTrial` is
 * `false` (correct — it cannot confirm the trial window), so its negation is
 * `true` — a purchase door with zero evidence the cut-off passed. This function
 * asks the opposite question directly ("did it PASS?") and answers `false` on
 * the exact same missing data, mirroring `isLeadAgentExpired`'s
 * `since !== null && since >= 0` shape verbatim. The two PAUSED predicates
 * therefore fail in deliberately OPPOSITE directions on the same row — one
 * protects "don't claim it's off" (informational), the other protects "don't
 * open the till" (transactional) — and neither should be derived from the
 * other by negation, or a future edit to one silently flips the other's
 * safety direction.
 */
export function isLeadAgentPausedAndLapsed(
  acceptance: tLeadAgentAcceptance | null,
  now: Date = new Date(),
): boolean {
  if (acceptance?.state !== 'PAUSED') return false;
  if (acceptance.phase) return acceptance.phase === 'trial_ended';
  const since = daysSincePayment(acceptance.nextPaymentAt, now);
  return since !== null && since >= 0;
}

/**
 * True while the Lead Agent add-on is PAUSED, regardless of the cut-off date.
 * Drives the Subscription reactivation card, which offers reactivation both
 * before and after the cut-off. Owner gating, plan eligibility, and the
 * session-resolve latch live in the provider.
 */
export function isLeadAgentPaused(
  acceptance: tLeadAgentAcceptance | null,
): boolean {
  return acceptance?.state === 'PAUSED';
}

/**
 * RET-2153 — true when the row's state is REVOKED: the Owner ACCEPTED the
 * Lead Agent at some point and leadbilling later revoked the permission (PR
 * #1237, the `state='inactive' + accepted=true` row).
 *
 * Deliberately TERMINAL and deliberately NOT composed with `phase` or
 * `nextPaymentAt`, unlike the PAUSED pair above: a paused row keeps access
 * through a scheduled cut-off (a future date can still mean "active today"),
 * but a revoked row is reported `inactive` the instant leadbilling revokes it
 * — there is no pending cut-off to honor. The frozen vara sends this state in
 * three date shapes (undated, a past `nextPaymentAt`, and one in the FUTURE)
 * precisely to pin that a future anchor must NOT be read as "still active
 * until then" the way `isLeadAgentPausedWithinTrial` reads it for PAUSED.
 * Reusing either PAUSED predicate for REVOKED would silently reopen that
 * question for a state that has already answered it.
 *
 * Whether the CUSTOMER is offered the add-on again is a separate, plan-level
 * question governed by TA-11 — see `hasBillableSubscriptionForRevokedLeadAgent`
 * — and is intentionally not part of this predicate.
 */
export function isLeadAgentRevoked(
  acceptance: tLeadAgentAcceptance | null,
): boolean {
  return acceptance?.state === 'REVOKED';
}

/**
 * TA-11 (ratificada por la PM, 2026-09-18): a un permiso REVOCADO se le
 * vuelve a ofrecer el add-on SIEMPRE QUE SU PLAN DE HOY LO PERMITA. Revocar es
 * terminal para la FILA, no para el cliente — salvo que la revocación haya
 * venido justamente de que ya no puede tenerlo (pasó a Basic, o canceló),
 * caso en el que no se le vuelve a ofrecer.
 *
 * Esta función resuelve la mitad que más importa de esa regla: "¿hay hoy una
 * suscripción a la que colgarle el add-on?" — sólo ACTIVE y PAST_DUE cuentan,
 * y sólo si no hay una cancelación ya programada.
 *
 * OJO, esto NO espeja ningún guardia del backend: la vista de leadbilling
 * publica la fila REVOKED sin mirar la suscripción (su única condición es
 * `isBannerVisible`); el guardia `noBillableSubscription` vive en los casos de
 * uso de MUTACIÓN (preview / reactivate), o sea DESPUÉS del clic. El recorte
 * de display existe sólo aquí, y por eso tiene que estar bien.
 *
 * Y una divergencia que conviene nombrar: TA-11 asigna la clasificación a
 * `LeadAgentAccessStatus` ("la vista publica que la fila está revocada, el
 * acceso dice si puede tenerlo, el front compone"). Este predicado la RE-DERIVA
 * localmente desde la suscripción, o sea es una tercera copia de una decisión
 * que ya tiene dueño. Es un recorte acotado a propósito: consumir
 * /lead-agent/access es RET-1572 y traerlo aquí sería un endpoint nuevo. Cuando
 * ese re-source llegue, esta función se borra, no se amplía.
 *
 * Sin este recorte el front reabriría la puerta de compra para cuentas
 * CANCELLED / DELETED / INACTIVE / NO_PAID que ya no pueden comprarlo
 * — medido en producción hoy: de la cohorte revocada,
 * 8 Professional ACTIVE, 6 Professional CANCELLED, 1 Advanced CANCELLED y 2
 * Basic; sin el recorte le ofreceríamos el add-on a las 7 cuentas canceladas.
 *
 * PAST_DUE se incluye a propósito, por CONSISTENCIA con la cohorte PAUSED
 * (que ya ofrece el add-on en PAST_DUE sin este recorte): su preview de cobro
 * puede rebotar, pero ese roce es preexistente a este ticket y no se arregla
 * aquí.
 *
 * `pendingCancellation` cuenta como no-facturable aunque el estatus sea ACTIVE:
 * leadbilling NUNCA escribe el estatus SCHEDULED_CANCELLATION, así que una
 * cancelación programada llega como ACTIVE + pendingCancellation, y esa es la
 * representación REAL de "canceló". Además la propia pantalla de Suscripción
 * ya decidió esto para todo lo demás (`shouldShowAddOns` empieza por
 * `!pendingCancellation`): sin este término la tarjeta de recompra entraría por
 * otra puerta y contradiría a la página en la que vive — esconder todos los
 * add-ons y a la vez ofrecer éste. Medido: 1 workspace revocado hoy.
 *
 * DELIBERADAMENTE ESCOPEADO al camino REVOKED (RET-2153) — nunca a PAUSED.
 * PAUSED shippeó en RET-2152 bajo otra regla, y esa cohorte NO fue medida
 * contra este recorte; ensancharle la compuerta aquí sería un cambio de
 * comportamiento silencioso para un grupo que este ticket no tocó. Si un
 * futuro ticket quiere unificarlas, que lo decida con su propia medición.
 */
export function hasBillableSubscriptionForRevokedLeadAgent(
  status: eSubscriptionStatus | null | undefined,
  pendingCancellation: boolean | null | undefined,
): boolean {
  if (pendingCancellation === true) return false;
  return (
    status === eSubscriptionStatus.ACTIVE ||
    status === eSubscriptionStatus.PAST_DUE
  );
}

const LEAD_AGENT_REACTIVATE_STATUSES: readonly tLeadAgentReactivateStatus[] = [
  'ok',
  'requires_action',
  'failed',
  // RET-1649: send_invoice reactivation emits an OPEN invoice (paid later) →
  // provisioned only on the paid-invoice webhook. Without this, the parser would
  // reject 'pending' as unknown and the hook would show a defensive error even
  // though the invoice was emitted correctly.
  'pending',
];

/** Finite number (any sign) or null — distinct from the non-negative variant. */
function toNullableFiniteNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

/**
 * Typed adapter for the A11 `GET .../reactivate/preview` `data` (RET-1343).
 * Single permitted narrowing point for the preview wire payload (never
 * `as unknown as` elsewhere — docs/solutions/logic-errors/
 * unsafe-cast-masks-backend-shape-drift.md). Returns null when the payload is
 * missing a required field so the modal fails CLOSED (confirm disabled +
 * retry): we never fabricate an amount or a prorationDate. `targetTier` is
 * runtime-validated against the selectable tiers (docs/solutions/logic-errors/
 * truthy-invalid-enum-bypasses-fallback.md) so a drifted/unknown tier is a
 * hard parse failure, not a silent fallback that could mismatch the charge.
 */
export function parseLeadAgentReactivatePreview(
  raw: unknown,
): tLeadAgentReactivatePreview | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  const amountCents = toNullableFiniteNumber(source.amountCents);
  const prorationDate = toNullableFiniteNumber(source.prorationDate);
  const currency = toNullableString(source.currency);
  const targetTier = toNullableString(source.targetTier);

  if (amountCents === null || prorationDate === null) return null;
  if (!currency) return null;
  if (
    !targetTier ||
    !LEAD_AGENT_SELECTABLE_TIERS.includes(
      targetTier as tLeadAgentSelectableTier,
    )
  ) {
    return null;
  }

  // RET-1649: optional — absent on older backends; never fail the parse over it.
  const collectionMethod = toNullableString(source.collectionMethod);
  const phase = parseLeadAgentRenewalPhase(source.phase);

  return {
    amountCents,
    currency,
    prorationDate,
    targetTier: targetTier as tLeadAgentSelectableTier,
    collectionMethod,
    phase,
  };
}

/**
 * Typed adapter for the A11 `POST .../reactivate` `data` (RET-1343). Single
 * permitted narrowing point for the result wire payload. `status` is
 * runtime-validated against the allowed set — an unknown/missing status is a
 * hard parse failure (returns null), which the hook treats as a defensive
 * `error` rather than guessing a terminal branch. `paymentIntentClientSecret`,
 * `invoiceId` and `message` are optional and normalised to null (Go zero-value
 * "" counts as absent via `toNullableString`).
 */
export function parseLeadAgentReactivateResult(
  raw: unknown,
): tLeadAgentReactivateResult | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  const status = toNullableString(source.status);
  const targetTier = toNullableString(source.targetTier);

  if (
    !status ||
    !LEAD_AGENT_REACTIVATE_STATUSES.includes(
      status as tLeadAgentReactivateStatus,
    )
  ) {
    return null;
  }
  if (
    !targetTier ||
    !LEAD_AGENT_SELECTABLE_TIERS.includes(
      targetTier as tLeadAgentSelectableTier,
    )
  ) {
    return null;
  }

  return {
    status: status as tLeadAgentReactivateStatus,
    targetTier: targetTier as tLeadAgentSelectableTier,
    paymentIntentClientSecret: toNullableString(
      source.paymentIntentClientSecret,
    ),
    invoiceId: toNullableString(source.invoiceId),
    code: toNullableString(source.code),
    message: toNullableString(source.message),
  };
}

const LEAD_AGENT_TIER_CHANGE_STATUSES: readonly tLeadAgentTierChangeStatus[] = [
  'ok',
  'requires_action',
  'failed',
];

/** Validated selectable tier, or null when missing/unknown (never throws). */
export const resolveSelectableTier = (
  value: unknown,
): tLeadAgentSelectableTier | null => {
  const tier = toNullableString(value);
  return tier &&
    LEAD_AGENT_SELECTABLE_TIERS.includes(tier as tLeadAgentSelectableTier)
    ? (tier as tLeadAgentSelectableTier)
    : null;
};

/**
 * Typed adapter for the A8 `GET .../tier-change/preview` `data` (RET-1614).
 * Mirrors `parseLeadAgentReactivatePreview`: single permitted narrowing point,
 * fails CLOSED (returns null) when a load-bearing field is missing so the modal
 * never fabricates an amount or prorationDate. `targetTier` is runtime-validated
 * (a drifted tier is a hard failure). `currentTier` is informational and
 * normalised to null when absent/unknown — the modal falls back to the tier it
 * already holds rather than blocking confirm.
 */
export function parseLeadAgentTierChangePreview(
  raw: unknown,
): tLeadAgentTierChangePreview | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  // amountCents uses the NON-NEGATIVE guard (not the finite one): an upgrade's
  // immediate prorated charge is never negative, so a backend anomaly returning
  // a negative amount fails CLOSED (preview null → error/retry) rather than
  // rendering a confirmable "pay -$X" credit-on-upgrade.
  const amountCents = toNullableNonNegativeNumber(source.amountCents);
  const prorationDate = toNullableFiniteNumber(source.prorationDate);
  const currency = toNullableString(source.currency);
  const targetTier = toNullableString(source.targetTier);

  if (amountCents === null || prorationDate === null) return null;
  if (!currency) return null;
  if (
    !targetTier ||
    !LEAD_AGENT_SELECTABLE_TIERS.includes(
      targetTier as tLeadAgentSelectableTier,
    )
  ) {
    return null;
  }

  return {
    amountCents,
    currency,
    prorationDate,
    currentTier: resolveSelectableTier(source.currentTier),
    targetTier: targetTier as tLeadAgentSelectableTier,
  };
}

/**
 * Typed adapter for the A8 `POST .../tier-change` `data` (RET-1614). Mirrors
 * `parseLeadAgentReactivateResult`: `status` is runtime-validated against the
 * allowed set (unknown/missing → null → the hook treats it as a defensive
 * `error` rather than guessing a terminal branch). `currentTier` is nullable;
 * the optional string fields normalise Go zero-value "" to null.
 */
export function parseLeadAgentTierChangeResult(
  raw: unknown,
): tLeadAgentTierChangeResult | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  const status = toNullableString(source.status);
  const targetTier = toNullableString(source.targetTier);

  if (
    !status ||
    !LEAD_AGENT_TIER_CHANGE_STATUSES.includes(
      status as tLeadAgentTierChangeStatus,
    )
  ) {
    return null;
  }
  if (
    !targetTier ||
    !LEAD_AGENT_SELECTABLE_TIERS.includes(
      targetTier as tLeadAgentSelectableTier,
    )
  ) {
    return null;
  }

  return {
    status: status as tLeadAgentTierChangeStatus,
    currentTier: resolveSelectableTier(source.currentTier),
    targetTier: targetTier as tLeadAgentSelectableTier,
    paymentIntentClientSecret: toNullableString(
      source.paymentIntentClientSecret,
    ),
    invoiceId: toNullableString(source.invoiceId),
    message: toNullableString(source.message),
  };
}

function toNullableNonNegativeNumber(value: unknown): number | null {
  return typeof value === 'number' && Number.isFinite(value) && value >= 0
    ? value
    : null;
}

function toNullableString(value: unknown): string | null {
  // Go zero-value "" is NOT nullish — treat it as absent explicitly
  // (docs/solutions/logic-errors/nullish-coalescing-preserves-go-zero-value-empty-string.md).
  return typeof value === 'string' && value !== '' ? value : null;
}

/**
 * A single `tierFit` entry validated to EXACTLY `{tier, exhaustsAroundDay}` —
 * any extra field an upstream might leak (`cap`, `tokens`, `covers`) is
 * dropped by construction, never copied through. `tier` must be a selectable
 * tier (the `NONE` DB sentinel is not a valid tierFit entry). `exhaustsAroundDay`
 * must be present as either an explicit `null` or an integer in 1..30 — a
 * missing key, a non-integer, an out-of-range day, or a non-object entry all
 * fail this single entry, which (below) fails the WHOLE array closed: tierFit
 * is a hint, never load-bearing, so one malformed row voids the batch rather
 * than risk painting a warning next to entries we could not validate.
 */
function parseLeadAgentTierFitEntry(raw: unknown): tLeadAgentTierFit | null {
  if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
    return null;
  }
  const source = raw as Record<string, unknown>;

  const tier = resolveSelectableTier(source.tier);
  if (!tier) return null;

  if (!('exhaustsAroundDay' in source)) return null;
  const day = source.exhaustsAroundDay;
  if (day === null) {
    return { tier, exhaustsAroundDay: null };
  }
  if (
    typeof day !== 'number' ||
    !Number.isInteger(day) ||
    day < 1 ||
    day > 30
  ) {
    return null;
  }

  return { tier, exhaustsAroundDay: day };
}

/**
 * Typed adapter for `data.tierFit` (RET-2118). `tierFit` is NOT load-bearing —
 * it only decides whether ConfirmTierModal paints a below-recommendation
 * warning — so this fails CLOSED to `null` (no warning, no crash) on anything
 * that isn't a clean array of valid entries: absent, explicit `null`, a
 * non-array, or an array containing even one malformed entry. Order is
 * preserved exactly as it arrives (the caller looks up by tier, not index).
 */
function parseLeadAgentTierFit(raw: unknown): tLeadAgentTierFit[] | null {
  if (!Array.isArray(raw)) return null;

  const parsed: tLeadAgentTierFit[] = [];
  for (const entry of raw) {
    const item = parseLeadAgentTierFitEntry(entry);
    if (!item) return null;
    parsed.push(item);
  }
  return parsed;
}

/**
 * RET-2118 — the below-recommendation warning's day, or `null` when no
 * warning should paint. The rule (docs/loop/RET-2118-ESTADO.md): the trigger
 * is `selectedTier < suggestedTier` (ordinal on `LEAD_AGENT_SELECTABLE_TIERS`,
 * which the front already computes elsewhere — never re-derived here), and
 * the content is THAT tier's own `tierFit` entry. `tierFit` absent/null
 * (older BFF, or a parser fail-closed) yields null — "behaves exactly as
 * today". Never recomputes the projection, tokens, or caps — those live in
 * workspace-service's `LeadAgentTierProjector` (MLA-05); this only reads the
 * day the backend already computed.
 */
export function resolveTierFitWarningDay(options: {
  selectedTier: tLeadAgentSelectableTier;
  suggestedTier: tLeadAgentSelectableTier;
  tierFit: tLeadAgentTierFit[] | null | undefined;
}): number | null {
  if (!options.tierFit) return null;

  const selectedIndex = LEAD_AGENT_SELECTABLE_TIERS.indexOf(
    options.selectedTier,
  );
  const suggestedIndex = LEAD_AGENT_SELECTABLE_TIERS.indexOf(
    options.suggestedTier,
  );
  if (selectedIndex === -1 || suggestedIndex === -1) return null;
  if (selectedIndex >= suggestedIndex) return null;

  const entry = options.tierFit.find((f) => f.tier === options.selectedTier);
  return entry?.exhaustsAroundDay ?? null;
}

/**
 * Typed adapter from the BFF wire payload to `tLeadAgentAcceptance`.
 * Single permitted narrowing point — never `as unknown as` the response
 * elsewhere (docs/solutions/logic-errors/unsafe-cast-masks-backend-shape-drift.md).
 * Returns null when the payload does not look like an acceptance row, which
 * downstream means "banner hidden" — the safe failure direction.
 */
export function parseLeadAgentAcceptance(
  raw: unknown,
): tLeadAgentAcceptance | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  const id = toNullableString(source.id);
  const workspaceId = toNullableString(source.workspaceId);
  const tier = toNullableString(source.tier);
  const suggestedTierRaw = toNullableString(source.suggestedTier);
  const state = toNullableString(source.state);

  if (!id || !workspaceId) return null;
  // Runtime-validate enums from external input — `as` alone is compile-time
  // only (docs/solutions/logic-errors/truthy-invalid-enum-bypasses-fallback.md).
  if (!tier || !LEAD_AGENT_TIERS.includes(tier as tLeadAgentTier)) return null;
  // suggestedTier carries the offer banner's recommended tier. Fall back to the
  // (already-validated) chosen tier when the backend omits it or sends an
  // unknown value, so the recommendation never blanks during the BFF rollout.
  const suggestedTier =
    suggestedTierRaw &&
    LEAD_AGENT_TIERS.includes(suggestedTierRaw as tLeadAgentTier)
      ? (suggestedTierRaw as tLeadAgentTier)
      : (tier as tLeadAgentTier);
  if (
    !state ||
    !LEAD_AGENT_STATES.includes(state as tLeadAgentAcceptanceState)
  ) {
    return null;
  }

  const rawMetrics =
    typeof source.precalculatedData === 'object' &&
    source.precalculatedData !== null
      ? (source.precalculatedData as Record<string, unknown>)
      : {};

  const metrics: tLeadAgentAcceptanceMetrics = {
    engagedLeadsCount: toNullableNonNegativeNumber(
      rawMetrics.engagedLeadsCount,
    ),
    leadAgentMessagesCount: toNullableNonNegativeNumber(
      rawMetrics.leadAgentMessagesCount,
    ),
    outOfHoursAttendedLeadsCount: toNullableNonNegativeNumber(
      rawMetrics.outOfHoursAttendedLeadsCount,
    ),
    averageFirstResponseSeconds: toNullableNonNegativeNumber(
      rawMetrics.averageFirstResponseSeconds,
    ),
  };

  const rawBucket = toNullableString(rawMetrics.workspaceBucket);
  const workspaceBucket = WORKSPACE_BUCKETS.includes(
    rawBucket as tLeadAgentWorkspaceBucket,
  )
    ? (rawBucket as tLeadAgentWorkspaceBucket)
    : null;

  // Absent/unknown origin falls back to PRESALE_COHORT so the banner keeps
  // its pre-RET-1220 behavior while older BFF deploys omit the field.
  const rawOrigin = toNullableString(source.origin);
  const origin = LEAD_AGENT_ORIGINS.includes(
    rawOrigin as tLeadAgentAcceptanceOrigin,
  )
    ? (rawOrigin as tLeadAgentAcceptanceOrigin)
    : 'PRESALE_COHORT';

  const phase = parseLeadAgentRenewalPhase(source.phase);

  return {
    id,
    workspaceId,
    tier: tier as tLeadAgentTier,
    suggestedTier,
    state: state as tLeadAgentAcceptanceState,
    origin,
    metrics,
    workspaceBucket,
    nextPaymentAt: toNullableString(source.nextPaymentAt),
    phase,
    dismissedAt: toNullableString(source.dismissedAt),
    tierFit: parseLeadAgentTierFit(source.tierFit),
  };
}

/** The offer banner renders only while the row is pending a decision. */
export function isAcceptancePending(
  acceptance: tLeadAgentAcceptance | null,
): boolean {
  return acceptance?.state === 'NONE';
}

/**
 * The informative variant (>14 days to first charge) is never shown on the
 * pre-acceptance offer banner (C2): the first banner appears at D-14 (fomo) for
 * EVERY cohort. Previously only POST_LAUNCH suppressed it while PRESALE_COHORT
 * saw it early — that early banner is the bug (RET-1452, Fanny 2026-06-19;
 * supersedes the POST_LAUNCH-only rule of RET-1216). resolveBannerVariant is
 * unchanged; only this gate widened to all origins, so it no longer depends on
 * the acceptance row.
 *
 * NOTE: gates C2 only. The bot-off free-activation nudge (C6,
 * isActivatableTrial) intentionally does NOT consult this — see the provider.
 */
export function isInformativeBannerSuppressed(
  variant: tLeadAgentBannerVariant,
): boolean {
  return variant === 'informative';
}

/**
 * RET-1288 — the Lead Agent add-on is only purchasable on Pro/Advanced plans
 * (the LEAD_AGENT_* SKUs are linked as `purchased_addon` to Pro/Advanced only).
 * On Basic/Legacy the acceptance banner would render but the tier modal degrades
 * to "couldn't load the plans", so gate visibility on plan. Anything that is not
 * Pro/Advanced fails closed — including the `PLAN_LEGACY` summary loading default
 * and a `null`/`undefined` planCode — so the banner never flashes before the real
 * plan resolves. Lives in `isBannerVisible` (not the component) so the
 * legacy-banner suppression keyed on it stays consistent on Basic. Delegates to
 * the shared `isProOrAdvancedPlan` predicate so every plan gate stays in sync.
 */
export function isPlanEligibleForLeadAgent(
  planCode: ePlanCode | null | undefined,
): boolean {
  return isProOrAdvancedPlan(planCode);
}

/* -------------------------------------------------------------------------- */
/* RET-1633 (U9) — Lead Agent trial-cap unlock offer                          */
/* -------------------------------------------------------------------------- */

export const LEAD_AGENT_UNLOCK_PHASES: readonly tLeadAgentUnlockPhase[] = [
  'none',
  'sighting',
  'capped',
  'extended_capped',
];

export const LEAD_AGENT_UNLOCK_OPTIONS: readonly tLeadAgentUnlockOption[] = [
  'unlock_now',
  'accept_deferred',
  'decline',
];

/**
 * The winning trial-cap banner variant, or null when no trial-cap banner should
 * render. `extendedCapped` is the CS-referral state.
 */
export type tTrialCapBannerVariant =
  | 'sighting'
  | 'capped'
  | 'extendedCapped'
  | null;

/** Validated priced tier entry, or null when a load-bearing field is malformed. */
function parseLeadAgentUnlockTierOffer(
  raw: unknown,
): tLeadAgentUnlockTierOffer | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  const tier = resolveSelectableTier(source.tier);
  const listAmountCents = toNullableNonNegativeNumber(source.listAmountCents);
  const unlockAmountCents = toNullableNonNegativeNumber(
    source.unlockAmountCents,
  );
  const couponPct = toNullableFiniteNumber(source.couponPct);
  const currency = toNullableString(source.currency);

  if (
    !tier ||
    listAmountCents === null ||
    unlockAmountCents === null ||
    couponPct === null ||
    !currency
  ) {
    return null;
  }

  return { tier, listAmountCents, unlockAmountCents, couponPct, currency };
}

/**
 * Typed adapter for the bff-web `GET .../lead-agent/unlock-offer` `data`
 * (RET-1633 / U8). Single permitted narrowing point — never `as unknown as` the
 * offer elsewhere (docs/solutions/logic-errors/unsafe-cast-masks-backend-shape-drift.md).
 *
 * Fails CLOSED to null on a malformed payload so a shape drift hides the
 * trial-cap banners rather than crashing. An ineligible workspace
 * (`{eligible:false}`) parses to the normalised empty offer (a valid "no banner"
 * answer, not an error). For an eligible offer the phase is load-bearing and
 * runtime-validated (an unknown phase / option / tier entry fails the whole
 * offer, mirroring the reactivate/tier-change parsers).
 */
export function parseLeadAgentUnlockOffer(
  raw: unknown,
): tLeadAgentUnlockOffer | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  if (typeof source.eligible !== 'boolean') return null;

  if (!source.eligible) {
    return {
      eligible: false,
      phase: null,
      trialEnd: null,
      acceptedTier: null,
      options: [],
      suggestedTier: null,
      tiers: [],
      extended: false,
    };
  }

  const phaseRaw = toNullableString(source.phase);
  if (
    !phaseRaw ||
    !LEAD_AGENT_UNLOCK_PHASES.includes(phaseRaw as tLeadAgentUnlockPhase)
  ) {
    return null;
  }

  const rawOptions = Array.isArray(source.options) ? source.options : [];
  const options: tLeadAgentUnlockOption[] = [];
  for (const option of rawOptions) {
    if (
      typeof option !== 'string' ||
      !LEAD_AGENT_UNLOCK_OPTIONS.includes(option as tLeadAgentUnlockOption)
    ) {
      return null;
    }
    options.push(option as tLeadAgentUnlockOption);
  }

  const rawTiers = Array.isArray(source.tiers) ? source.tiers : [];
  const tiers: tLeadAgentUnlockTierOffer[] = [];
  for (const entry of rawTiers) {
    const parsed = parseLeadAgentUnlockTierOffer(entry);
    if (!parsed) return null;
    tiers.push(parsed);
  }

  return {
    eligible: true,
    phase: phaseRaw as tLeadAgentUnlockPhase,
    trialEnd: toNullableString(source.trialEnd),
    acceptedTier: resolveSelectableTier(source.acceptedTier),
    options,
    suggestedTier: resolveSelectableTier(source.suggestedTier),
    tiers,
    extended: source.extended === true,
  };
}

/**
 * Resolve the winning trial-cap banner variant. Pure — the provider ANDs the
 * role/plan/session gates around the result.
 *
 * Order: never-activated (C6) and expired (C7) win over the whole trial-cap
 * surface (return null); then extended_capped → CS referral; otherwise
 * capped > sighting. That is the whole rule now.
 *
 * NO LAST-DAYS EXCEPTION ANY MORE (Fanny's ruling, 2026-09-01). §18 carried
 * one — in the final days the countdown was to win over the cap, because
 * "vender el desbloqueo por 2-3 días es mal negocio" — and the prices say
 * otherwise: by `MLA-77` the unlock charges the SAME first bridge the deferred
 * path charges at trial end, and paying it IS accepting the tier, reusing the
 * scheduled charge rather than adding one. So unlocking with two days left
 * costs nothing extra; it only starts the paid month sooner and brings the
 * agent back in the meantime.
 *
 * With no price penalty there is nothing to protect the customer from, and the
 * capped offer already carries BOTH doors — `unlock_now` and `accept_deferred`.
 * Standing down, or dropping the unlock option, was us making that choice for
 * them. The cap wins and the customer decides; what the cap owes them is the
 * deadline, which now rides in its copy (see `leadAgentTrialDaysRemaining`).
 *
 * §18's exception paragraph contradicts this and needs correcting in the
 * catalog.
 */
export function resolveTrialCapBannerVariant(options: {
  phase: tLeadAgentUnlockPhase | null;
  daysUntilTrialEnd: number | null;
  neverActivated: boolean;
  isExpired: boolean;
}): tTrialCapBannerVariant {
  if (options.neverActivated || options.isExpired) return null;

  const { phase } = options;
  if (phase === null || phase === 'none') return null;
  if (phase === 'extended_capped') return 'extendedCapped';

  return phase === 'capped' ? 'capped' : 'sighting';
}

const LEAD_AGENT_UNLOCK_STATUSES: readonly tLeadAgentReactivateStatus[] = [
  'ok',
  'requires_action',
  'failed',
];

/**
 * Typed adapter for the bff-web `POST .../lead-agent/unlock` `data` (RET-1633 /
 * U10). Single permitted narrowing point — never `as unknown as` the result
 * elsewhere (docs/solutions/logic-errors/unsafe-cast-masks-backend-shape-drift.md).
 *
 * `status` is load-bearing and runtime-validated against the charge-status union
 * (`ok | requires_action | failed`): a drifted or absent status fails CLOSED
 * (returns null) so the modal treats it as a generic retryable error rather than
 * fabricating a success and telling the Owner the bot is back when it is not.
 * Every other field is informational and normalised to null/false when absent.
 */
export function parseLeadAgentUnlockResult(
  raw: unknown,
): tLeadAgentUnlockResult | null {
  if (typeof raw !== 'object' || raw === null) return null;
  const source = raw as Record<string, unknown>;

  const status = toNullableString(source.status);
  if (
    !status ||
    !LEAD_AGENT_UNLOCK_STATUSES.includes(status as tLeadAgentReactivateStatus)
  ) {
    return null;
  }

  return {
    status: status as tLeadAgentUnlockResult['status'],
    action: toNullableString(source.action),
    tier: resolveSelectableTier(source.tier),
    limit: toNullableNonNegativeNumber(source.limit),
    provisionPending: source.provisionPending === true,
    clientSecret: toNullableString(source.clientSecret),
    paymentIntentId: toNullableString(source.paymentIntentId),
    message: toNullableString(source.message),
  };
}
